import React, { useState, useCallback, useContext } from "react";
import { Redirect } from "react-router-dom";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
	Button,
	TableBody,
	TableRow,
	TableCell,
	IconButton,
	Table,
	TableHead,
	Paper,
	Tooltip,
	Typography,
	CircularProgress,
} from "@material-ui/core";
import {
	Edit,
	CheckCircle,
	SignalCellularConnectedNoInternet2Bar,
	SignalCellularConnectedNoInternet0Bar,
	SignalCellular4Bar,
	CropFree,
	DeleteOutline,
	Equalizer,
	RateReview,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import AiUsageModal from "../../components/AiUsageModal";
import MetaAdsModal from "../../components/MetaAdsModal";
import NpsReportModal from "../../components/NpsReportModal";
import ApiDocsModal from "../../components/ApiDocsModal";
import HolidaysModal from "../../components/HolidaysModal";
import SendIcon from "@material-ui/icons/Send";
import BeachAccessIcon from "@material-ui/icons/BeachAccess";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	mainPaper: {
		flex: 1,
		padding: theme.spacing(1),
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	customTableCell: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	tooltip: {
		backgroundColor: "#f5f5f9",
		color: "rgba(0, 0, 0, 0.87)",
		fontSize: theme.typography.pxToRem(14),
		border: "1px solid #dadde9",
		maxWidth: 450,
	},
	tooltipPopper: {
		textAlign: "center",
	},
	buttonProgress: {
		color: green[500],
	},
}));

const CustomToolTip = ({ title, content, children }) => {
	const classes = useStyles();

	return (
		<Tooltip
			arrow
			classes={{
				tooltip: classes.tooltip,
				popper: classes.tooltipPopper,
			}}
			title={
				<React.Fragment>
					<Typography gutterBottom color="inherit">
						{title}
					</Typography>
					{content && <Typography>{content}</Typography>}
				</React.Fragment>
			}
		>
			{children}
		</Tooltip>
	);
};

const Connections = () => {
	const classes = useStyles();

	const { user } = useContext(AuthContext);
	const { whatsApps, loading } = useContext(WhatsAppsContext);
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [aiUsageModalOpen, setAiUsageModalOpen] = useState(false);
	const [metaAdsModalOpen, setMetaAdsModalOpen] = useState(false);
	const [npsModalOpen, setNpsModalOpen] = useState(false);
	const [apiDocsModalOpen, setApiDocsModalOpen] = useState(false);
	const [holidaysModalOpen, setHolidaysModalOpen] = useState(false);
	const [selectedNpsWhatsappId, setSelectedNpsWhatsappId] = useState(null);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
	const [selectedAiWhatsApp, setSelectedAiWhatsApp] = useState(null);
	const [selectedAdsWhatsApp, setSelectedAdsWhatsApp] = useState(null);
	const [selectedHolidaysWhatsApp, setSelectedHolidaysWhatsApp] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const confirmationModalInitialState = {
		action: "",
		title: "",
		message: "",
		whatsAppId: "",
		open: false,
	};
	const [confirmModalInfo, setConfirmModalInfo] = useState(
		confirmationModalInitialState
	);

	const getWhatsAppPhone = whatsApp => {
		try {
			if (whatsApp?.session) {
				const parsed = JSON.parse(whatsApp.session);
				const jid = parsed?.me?.id || "";
				const clean = jid.split(":")[0].split("@")[0].replace(/\D/g, "");
				if (clean) return clean;
			}
		} catch (e) {}
		return "";
	};

	const handleCopyPhone = phone => {
		navigator.clipboard.writeText(phone);
		toast.success(`Número ${phone} copiado!`);
	};

	const handleOpenHolidaysModal = whatsApp => {
		setSelectedHolidaysWhatsApp(whatsApp);
		setHolidaysModalOpen(true);
	};

	const handleCloseHolidaysModal = useCallback(() => {
		setHolidaysModalOpen(false);
		setSelectedHolidaysWhatsApp(null);
	}, []);

	const handleOpenNpsModal = (whatsAppId = null) => {
		setSelectedNpsWhatsappId(whatsAppId);
		setNpsModalOpen(true);
	};

	const handleCloseNpsModal = useCallback(() => {
		setNpsModalOpen(false);
		setSelectedNpsWhatsappId(null);
	}, []);

	const handleOpenAiUsageModal = whatsApp => {
		setSelectedAiWhatsApp(whatsApp);
		setAiUsageModalOpen(true);
	};

	const handleCloseAiUsageModal = useCallback(() => {
		setAiUsageModalOpen(false);
		setSelectedAiWhatsApp(null);
	}, []);

	const handleOpenMetaAdsModal = whatsApp => {
		setSelectedAdsWhatsApp(whatsApp);
		setMetaAdsModalOpen(true);
	};

	const handleCloseMetaAdsModal = useCallback(() => {
		setMetaAdsModalOpen(false);
		setSelectedAdsWhatsApp(null);
	}, []);

	const handleStartWhatsAppSession = async whatsAppId => {
		try {
			await api.post(`/whatsappsession/${whatsAppId}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleRequestNewQrCode = async whatsAppId => {
		try {
			await api.put(`/whatsappsession/${whatsAppId}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenWhatsAppModal = () => {
		setSelectedWhatsApp(null);
		setWhatsAppModalOpen(true);
	};

	const handleCloseWhatsAppModal = useCallback(() => {
		setWhatsAppModalOpen(false);
		setSelectedWhatsApp(null);
	}, [setSelectedWhatsApp, setWhatsAppModalOpen]);

	const handleOpenQrModal = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	};

	const handleCloseQrModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setQrModalOpen(false);
	}, [setQrModalOpen, setSelectedWhatsApp]);

	const handleEditWhatsApp = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setWhatsAppModalOpen(true);
	};

	const handleOpenConfirmationModal = (action, whatsAppId) => {
		if (action === "disconnect") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.disconnectTitle"),
				message: i18n.t("connections.confirmationModal.disconnectMessage"),
				whatsAppId: whatsAppId,
			});
		}

		if (action === "delete") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.deleteTitle"),
				message: i18n.t("connections.confirmationModal.deleteMessage"),
				whatsAppId: whatsAppId,
			});
		}
		setConfirmModalOpen(true);
	};

	const handleSubmitConfirmationModal = async () => {
		if (confirmModalInfo.action === "disconnect") {
			try {
				await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
			} catch (err) {
				toastError(err);
			}
		}

		if (confirmModalInfo.action === "delete") {
			try {
				await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
				toast.success(i18n.t("connections.toasts.deleted"));
			} catch (err) {
				toastError(err);
			}
		}

		setConfirmModalInfo(confirmationModalInitialState);
	};

	const renderActionButtons = whatsApp => {
		return (
			<>
				{whatsApp.status === "qrcode" && (
					<Button
						size="small"
						variant="contained"
						color="primary"
						onClick={() => handleOpenQrModal(whatsApp)}
					>
						{i18n.t("connections.buttons.qrcode")}
					</Button>
				)}
				{whatsApp.status === "DISCONNECTED" && (
					<>
						<Button
							size="small"
							variant="outlined"
							color="primary"
							onClick={() => handleStartWhatsAppSession(whatsApp.id)}
						>
							{i18n.t("connections.buttons.tryAgain")}
						</Button>{" "}
						<Button
							size="small"
							variant="outlined"
							color="secondary"
							onClick={() => handleRequestNewQrCode(whatsApp.id)}
						>
							{i18n.t("connections.buttons.newQr")}
						</Button>
					</>
				)}
				{(whatsApp.status === "CONNECTED" ||
					whatsApp.status === "PAIRING" ||
					whatsApp.status === "TIMEOUT") && (
					<Button
						size="small"
						variant="outlined"
						color="secondary"
						onClick={() => {
							handleOpenConfirmationModal("disconnect", whatsApp.id);
						}}
					>
						{i18n.t("connections.buttons.disconnect")}
					</Button>
				)}
				{whatsApp.status === "OPENING" && (
					<>
						<Button size="small" variant="outlined" disabled color="default">
							{i18n.t("connections.buttons.connecting")}
						</Button>
						<Button
							size="small"
							variant="outlined"
							color="secondary"
							onClick={() => handleRequestNewQrCode(whatsApp.id)}
						>
							{i18n.t("connections.buttons.newQr")}
						</Button>
						<Button
							size="small"
							variant="outlined"
							color="secondary"
							onClick={() => {
								handleOpenConfirmationModal("disconnect", whatsApp.id);
							}}
						>
							{i18n.t("connections.buttons.disconnect")}
						</Button>
					</>
				)}
			</>
		);
	};

	const renderStatusToolTips = whatsApp => {
		return (
			<div className={classes.customTableCell}>
				{whatsApp.status === "DISCONNECTED" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.disconnected.title")}
						content={i18n.t("connections.toolTips.disconnected.content")}
					>
						<SignalCellularConnectedNoInternet0Bar color="secondary" />
					</CustomToolTip>
				)}
				{whatsApp.status === "OPENING" && (
					<CircularProgress size={24} className={classes.buttonProgress} />
				)}
				{whatsApp.status === "qrcode" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.qrcode.title")}
						content={i18n.t("connections.toolTips.qrcode.content")}
					>
						<CropFree />
					</CustomToolTip>
				)}
				{whatsApp.status === "CONNECTED" && (
					<CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
						<SignalCellular4Bar style={{ color: green[500] }} />
					</CustomToolTip>
				)}
				{(whatsApp.status === "TIMEOUT" || whatsApp.status === "PAIRING") && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.timeout.title")}
						content={i18n.t("connections.toolTips.timeout.content")}
					>
						<SignalCellularConnectedNoInternet2Bar color="secondary" />
					</CustomToolTip>
				)}
			</div>
		);
	};

	if (user.profile !== "admin") {
		return <Redirect to="/" />;
	}

	return (
		<MainContainer>
			<ConfirmationModal
				title={confirmModalInfo.title}
				open={confirmModalOpen}
				onClose={setConfirmModalOpen}
				onConfirm={handleSubmitConfirmationModal}
			>
				{confirmModalInfo.message}
			</ConfirmationModal>
			<QrcodeModal
				open={qrModalOpen}
				onClose={handleCloseQrModal}
				whatsAppId={qrModalOpen && selectedWhatsApp?.id}
			/>
			<WhatsAppModal
				open={whatsAppModalOpen}
				onClose={handleCloseWhatsAppModal}
				whatsAppId={whatsAppModalOpen && selectedWhatsApp?.id}
			/>
			{aiUsageModalOpen && selectedAiWhatsApp && (
				<AiUsageModal
					open={aiUsageModalOpen}
					onClose={handleCloseAiUsageModal}
					whatsApp={selectedAiWhatsApp}
				/>
			)}
			{metaAdsModalOpen && selectedAdsWhatsApp && (
				<MetaAdsModal
					open={metaAdsModalOpen}
					onClose={handleCloseMetaAdsModal}
					whatsApp={selectedAdsWhatsApp}
					onUpdate={() => {
						// Força atualização da listagem
						window.location.reload();
					}}
				/>
			)}
			{npsModalOpen && (
				<NpsReportModal
					open={npsModalOpen}
					onClose={handleCloseNpsModal}
					defaultWhatsappId={selectedNpsWhatsappId}
				/>
			)}
			{apiDocsModalOpen && (
				<ApiDocsModal
					open={apiDocsModalOpen}
					onClose={() => setApiDocsModalOpen(false)}
					whatsApps={whatsApps}
				/>
			)}
			{holidaysModalOpen && selectedHolidaysWhatsApp && (
				<HolidaysModal
					open={holidaysModalOpen}
					onClose={handleCloseHolidaysModal}
					whatsApp={selectedHolidaysWhatsApp}
					onUpdate={() => {
						// Força atualização suave da listagem
						window.location.reload();
					}}
				/>
			)}
			<MainHeader>
				<Title>{i18n.t("connections.title")}</Title>
				<MainHeaderButtonsWrapper>
					<Button
						variant="outlined"
						style={{
							borderColor: "#0284c7",
							color: "#0369a1",
							fontWeight: 700,
							textTransform: "none",
							marginRight: 8
						}}
						startIcon={<SendIcon style={{ color: "#0284c7", fontSize: 18 }} />}
						onClick={() => setApiDocsModalOpen(true)}
					>
						🔌 Disparos API REST
					</Button>
					<Button
						variant="outlined"
						style={{
							borderColor: "#f59e0b",
							color: "#d97706",
							fontWeight: 700,
							textTransform: "none",
							marginRight: 8
						}}
						startIcon={<RateReview style={{ color: "#f59e0b" }} />}
						onClick={() => handleOpenNpsModal(null)}
					>
						⭐ Relatório NPS
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={handleOpenWhatsAppModal}
					>
						{i18n.t("connections.buttons.add")}
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>
			<Paper className={classes.mainPaper} variant="outlined">
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell align="center">
								{i18n.t("connections.table.name")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.status")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.session")}
							</TableCell>
							<TableCell align="center">
								Inteligência Artificial (IA)
							</TableCell>
							<TableCell align="center">
								Meta Ads (Tráfego Pago)
							</TableCell>
							<TableCell align="center">
								Pesquisa NPS
							</TableCell>
							<TableCell align="center">
								Feriados & Recessos
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.lastUpdate")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.default")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.actions")}
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRowSkeleton />
						) : (
							<>
								{whatsApps?.length > 0 &&
									whatsApps.map(whatsApp => (
										<TableRow key={whatsApp.id}>
											<TableCell align="center">
												<div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3 }}>
													<div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
														{whatsApp.color && (
															<span
																style={{
																	display: "inline-block",
																	width: 10,
																	height: 10,
																	borderRadius: "50%",
																	backgroundColor: whatsApp.color,
																}}
															/>
														)}
														<span style={{ fontWeight: 600 }}>{whatsApp.name}</span>
													</div>
													{(() => {
														const phone = getWhatsAppPhone(whatsApp);
														if (!phone) return null;
														return (
															<Tooltip title="Clique para copiar o número" arrow>
																<span
																	onClick={() => handleCopyPhone(phone)}
																	style={{
																		fontSize: "0.72rem",
																		color: "#475569",
																		cursor: "pointer",
																		display: "inline-flex",
																		alignItems: "center",
																		gap: 4,
																		padding: "1px 6px",
																		borderRadius: 4,
																		backgroundColor: "#f1f5f9",
																		border: "1px solid #e2e8f0",
																		fontFamily: "monospace",
																		fontWeight: 600,
																	}}
																>
																	{phone}
																	<FileCopyIcon style={{ fontSize: 11, color: "#64748b" }} />
																</span>
															</Tooltip>
														);
													})()}
												</div>
											</TableCell>
											<TableCell align="center">
												{renderStatusToolTips(whatsApp)}
											</TableCell>
											<TableCell align="center">
												{renderActionButtons(whatsApp)}
											</TableCell>
											<TableCell align="center">
												{whatsApp.useAi ? (
													<Button
														size="small"
														variant="outlined"
														style={{
															textTransform: "none",
															fontSize: "0.75rem",
															fontWeight: 700,
															color: "#2563eb",
															borderColor: "#bfdbfe",
															backgroundColor: "#eff6ff",
															borderRadius: 16,
															padding: "2px 10px",
														}}
														startIcon={<Equalizer style={{ fontSize: 16, color: "#2563eb" }} />}
														onClick={() => handleOpenAiUsageModal(whatsApp)}
													>
														IA Ativa 📊
													</Button>
												) : (
													<span style={{ fontSize: "0.75rem", color: "#94a3b8", fontWeight: 500 }}>
														Desativada
													</span>
												)}
											</TableCell>
											<TableCell align="center">
												{(() => {
													let adsCount = 0;
													if (whatsApp.metaAds) {
														try {
															const parsed = JSON.parse(whatsApp.metaAds);
															if (Array.isArray(parsed)) adsCount = parsed.length;
														} catch (e) {}
													}

													if (adsCount > 0) {
														return (
															<Button
																size="small"
																variant="outlined"
																style={{
																	textTransform: "none",
																	fontSize: "0.75rem",
																	fontWeight: 700,
																	color: "#1877F2",
																	borderColor: "#93c5fd",
																	backgroundColor: "#eff6ff",
																	borderRadius: 16,
																	padding: "2px 10px",
																}}
																onClick={() => handleOpenMetaAdsModal(whatsApp)}
															>
																🎯 {adsCount} Anúncio{adsCount > 1 ? "s" : ""} 📊
															</Button>
														);
													}

													return (
														<Button
															size="small"
															variant="text"
															style={{
																textTransform: "none",
																fontSize: "0.72rem",
																color: "#64748b",
																fontWeight: 500
															}}
															onClick={() => handleOpenMetaAdsModal(whatsApp)}
														>
															+ Configurar Ads
														</Button>
													);
												})()}
											</TableCell>
											<TableCell align="center">
												<Button
													size="small"
													variant="outlined"
													style={{
														textTransform: "none",
														fontSize: "0.75rem",
														fontWeight: 700,
														color: whatsApp.useNps ? "#b45309" : "#64748b",
														borderColor: whatsApp.useNps ? "#fde68a" : "#e2e8f0",
														backgroundColor: whatsApp.useNps ? "#fffbeb" : "#f8fafc",
														borderRadius: 16,
														padding: "2px 10px",
													}}
													startIcon={<RateReview style={{ fontSize: 15, color: whatsApp.useNps ? "#f59e0b" : "#94a3b8" }} />}
													onClick={() => handleOpenNpsModal(whatsApp.id)}
												>
													{whatsApp.useNps ? "Ver Notas ⭐" : "Notas (Off)"}
												</Button>
											</TableCell>
											<TableCell align="center">
												{(() => {
													let holidaysCount = 0;
													if (whatsApp.holidays) {
														try {
															const parsed = JSON.parse(whatsApp.holidays);
															if (Array.isArray(parsed)) holidaysCount = parsed.filter(h => h.active !== false).length;
														} catch (e) {}
													}

													if (holidaysCount > 0) {
														return (
															<Button
																size="small"
																variant="outlined"
																style={{
																	textTransform: "none",
																	fontSize: "0.75rem",
																	fontWeight: 700,
																	color: "#059669",
																	borderColor: "#a7f3d0",
																	backgroundColor: "#ecfdf5",
																	borderRadius: 16,
																	padding: "2px 10px",
																}}
																startIcon={<BeachAccessIcon style={{ fontSize: 15, color: "#059669" }} />}
																onClick={() => handleOpenHolidaysModal(whatsApp)}
															>
																🏖️ {holidaysCount} Feriado{holidaysCount > 1 ? "s" : ""} 📅
															</Button>
														);
													}

													return (
														<Button
															size="small"
															variant="text"
															style={{
																textTransform: "none",
																fontSize: "0.72rem",
																color: "#64748b",
																fontWeight: 500
															}}
															onClick={() => handleOpenHolidaysModal(whatsApp)}
														>
															+ Configurar Feriados
														</Button>
													);
												})()}
											</TableCell>
											<TableCell align="center">
												{format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
											</TableCell>
											<TableCell align="center">
												{whatsApp.isDefault && (
													<div className={classes.customTableCell}>
														<CheckCircle style={{ color: green[500] }} />
													</div>
												)}
											</TableCell>
											<TableCell align="center">
												<IconButton
													size="small"
													onClick={() => handleEditWhatsApp(whatsApp)}
												>
													<Edit />
												</IconButton>

												<IconButton
													size="small"
													onClick={e => {
														handleOpenConfirmationModal("delete", whatsApp.id);
													}}
												>
													<DeleteOutline />
												</IconButton>
											</TableCell>
										</TableRow>
									))}
							</>
						)}
					</TableBody>
				</Table>
			</Paper>
		</MainContainer>
	);
};

export default Connections;
